import type { ApiConfig } from '@api/types';
import { typedMethodPost, typedMethodGet, typedMethodPostWithRequiredParams } from '@api/utils';
import type {
  UserProfileDetailsResponseDto,
  GetAllFeatureFlagsResponseDto,
  UserDto,
  ChangePasswordRequestDto,
  ChangePasswordResponseDto,
  PasswordResetRequestDto,
  PasswordResetResponseDto,
  VerifyPasswordResetRequestDto,
  VerifyPasswordResetResponseDto,
  ResetPasswordRequestDto,
  ResetPasswordResponseDto,
  ConfirmUserInviteRequestDto,
  ConfirmUserInviteResponseDto,
  VerifyEmailRequestDto,
  VerifyEmailResponseDto,
  UserInfoDto,
} from '../../generated/models';
import type { GetUserParams, LoginBodyParams, LoginResponse as LoginResponseDto, RegisterRequest } from './types';

import type {
  UserProfileDetailsResponse,
  GetAllFeatureFlagsResponse,
  User,
  ChangePasswordResponse,
  PasswordResetResponse,
  VerifyPasswordResetResponse,
  ResetPasswordResponse,
  ConfirmUserInviteResponse,
  VerifyEmailResponse,
  UserInfo,
  LoginResponse,
} from '../../domain/types';

import {
  toUserProfileDetailsResponse,
  toGetAllFeatureFlagsResponse,
  toUser,
  toChangePasswordResponse,
  toPasswordResetResponse,
  toVerifyPasswordResetResponse,
  toResetPasswordResponse,
  toConfirmUserInviteResponse,
  toVerifyEmailResponse,
  toUserInfo,
  toLoginResponse,
} from '../../domain/mappers';

export default ({ baseURL, baseURLIndentitiServer }: ApiConfig) => ({
  userProfileDetails: typedMethodPost<void, UserProfileDetailsResponseDto, UserProfileDetailsResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/userProfileDetails`, {
        method: 'POST',
        ...data,
      }),
    toUserProfileDetailsResponse
  ),
  getAllFeatureFlags: typedMethodPost<void, GetAllFeatureFlagsResponseDto, GetAllFeatureFlagsResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/getAllFeatureFlags`, {
        method: 'POST',
        ...data,
      }),
    toGetAllFeatureFlagsResponse
  ),
  users: typedMethodGet<GetUserParams, UserDto, User>(
    (data) =>
      fetch(`${baseURL}/v1/users/${data.params.id}`, {
        method: 'GET',
        headers: data.headers,
      }),
    toUser
  ),
  login: typedMethodPostWithRequiredParams<LoginBodyParams, LoginResponseDto, LoginResponse>(
    (data) =>
      fetch(`${baseURLIndentitiServer}/oauth2/v2.0/token`, {
        method: 'POST',
        ...data,
        body: data.params,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
      }),
    toLoginResponse
  ),
  userInfo: typedMethodGet<void, UserInfoDto, UserInfo>(
    (data) =>
      fetch(`${baseURL}/v1/membership/userinfo`, {
        method: 'GET',
        headers: data.headers,
      }),
    toUserInfo
  ),
  changePassword: typedMethodPost<ChangePasswordRequestDto, ChangePasswordResponseDto, ChangePasswordResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membership/changePassword`, {
        method: 'POST',
        ...data,
      }),
    toChangePasswordResponse
  ),
  forgotPassword: typedMethodPost<PasswordResetRequestDto, PasswordResetResponseDto, PasswordResetResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membershipOnboarding/requestForPasswordReset`, {
        method: 'POST',
        ...data,
      }),
    toPasswordResetResponse
  ),
  forgotPasswordCheckCode: typedMethodPost<
    VerifyPasswordResetRequestDto,
    VerifyPasswordResetResponseDto,
    VerifyPasswordResetResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/membershipOnboarding/verifyPasswordReset`, {
        method: 'POST',
        ...data,
      }),
    toVerifyPasswordResetResponse
  ),
  resetPassword: typedMethodPost<ResetPasswordRequestDto, ResetPasswordResponseDto, ResetPasswordResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membershipOnboarding/resetPassword`, {
        method: 'POST',
        ...data,
      }),
    toResetPasswordResponse
  ),
  confirmInviteVerify: typedMethodPost<
    ConfirmUserInviteRequestDto,
    ConfirmUserInviteResponseDto,
    ConfirmUserInviteResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/membershipOnboarding/confirmUserInvite`, {
        method: 'POST',
        ...data,
      }),
    toConfirmUserInviteResponse
  ),
  register: typedMethodPost<RegisterRequest, void>(
    (data) =>
      fetch(`${baseURL}/v1/users/register`, {
        method: 'POST',
        ...data,
      })
  ),
  checkStatus: typedMethodPost<VerifyEmailRequestDto, VerifyEmailResponseDto, VerifyEmailResponse>(
    (data) =>
      fetch(`${baseURL}/v1/membershipOnboarding/verifyEmail`, {
        method: 'POST',
        ...data,
      }),
    toVerifyEmailResponse
  ),
});

// Re-export domain types for consumers
export type {
  UserProfileDetailsResponse,
  GetAllFeatureFlagsResponse,
  User,
  ChangePasswordResponse,
  PasswordResetResponse,
  VerifyPasswordResetResponse,
  ResetPasswordResponse,
  ConfirmUserInviteResponse,
  VerifyEmailResponse,
  UserInfo,
  LoginResponse,
} from '../../domain/types';

// Re-export request DTOs (still needed for building requests)
export type {
  ChangePasswordRequestDto,
  PasswordResetRequestDto,
  VerifyPasswordResetRequestDto,
  ResetPasswordRequestDto,
  ConfirmUserInviteRequestDto,
  VerifyEmailRequestDto,
} from '../../generated/models';

export type { GetUserParams, LoginParams, LoginBodyParams, RegisterRequest } from './types';
